import { Dialog, DialogContent } from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import { TrackLocations } from "@/hooks/use-segment";
import { type DialogProps } from "@radix-ui/react-dialog";
import IconMagnifyingGlass from "central-icons/IconMagnifyingGlass";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import { useCallback, useEffect, useImperativeHandle, useRef } from "react";

const commandContext = React.createContext<{
    listRef: React.RefObject<React.ElementRef<typeof CommandPrimitive.List>>;
}>(null as unknown as { listRef: React.RefObject<React.ElementRef<typeof CommandPrimitive.List>> });

const Command = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => {
    const listRef = React.useRef<React.ElementRef<typeof CommandPrimitive.List>>(null);

    return (
        <commandContext.Provider value={{ listRef }}>
            <CommandPrimitive
                ref={ref}
                className={cn(
                    "flex size-full flex-col overflow-hidden rounded-lg border border-border-base bg-surface-popover p-0 shadow",
                    className,
                )}
                {...props}
            />
        </commandContext.Provider>
    );
});
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
    return (
        <Dialog {...props}>
            <DialogContent className="overflow-hidden p-0" data-track-location={TrackLocations.Command}>
                <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
                    {children}
                </Command>
            </DialogContent>
        </Dialog>
    );
};

type CommandInputProps = React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    containerClassName?: string;
    hideIcon?: boolean;
};
const CommandInput = React.forwardRef<React.ElementRef<typeof CommandPrimitive.Input>, CommandInputProps>(
    ({ className, containerClassName, hideIcon, onValueChange, ...props }, ref) => {
        const context = React.useContext(commandContext);
        const rafId = useRef(0);
        const onValueChangeRef = useRef(onValueChange);
        onValueChangeRef.current = onValueChange;

        // Stable callback — no allocation per render. Cancels previous rAF to avoid
        // queuing redundant scrollTo calls during fast typing (AGENT-687).
        const handleValueChange = useCallback(
            (value: string) => {
                cancelAnimationFrame(rafId.current);
                rafId.current = requestAnimationFrame(() => {
                    context.listRef.current?.scrollTo({ top: 0 });
                });
                onValueChangeRef.current?.(value);
            },
            [context.listRef],
        );

        // Cancel any pending rAF on unmount so the callback doesn't keep
        // the event loop alive in test environments (happy-dom + vitest threads pool).
        useEffect(() => () => cancelAnimationFrame(rafId.current), []);

        return (
            // the negative margin allows us to span the border across the entire command header. We cancel it out in the child
            <div className={cn("border-b border-border-base", containerClassName)}>
                <div
                    className="flex h-10 w-full items-center gap-2 px-3 py-2"
                    // eslint-disable-next-line react/no-unknown-property
                    cmdk-input-wrapper=""
                >
                    {!hideIcon && (
                        <div className="flex aspect-square size-6 items-center justify-center">
                            <IconMagnifyingGlass className="size-4 shrink-0 text-content-muted" />
                        </div>
                    )}
                    <CommandPrimitive.Input
                        ref={ref}
                        onValueChange={handleValueChange}
                        className={cn(
                            "flex h-5 w-full rounded-none bg-transparent py-0 text-base outline-none placeholder:text-content-muted focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50",
                            className,
                        )}
                        {...props}
                    />
                </div>
            </div>
        );
    },
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.List>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => {
    const context = React.useContext(commandContext);
    useImperativeHandle(ref, () => context.listRef.current!);

    return (
        <CommandPrimitive.List
            ref={context.listRef}
            className={cn("w-full overflow-y-auto overflow-x-hidden", className)}
            {...props}
        />
    );
});

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Empty>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>(({ className, ...props }, ref) => {
    return (
        <CommandPrimitive.Empty
            ref={ref}
            className={cn("px-2 py-1 text-start text-base text-content-muted", className)}
            {...props}
        />
    );
});

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandLoading = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Loading>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Loading>
>(({ className, ...props }, ref) => {
    return (
        <CommandPrimitive.Loading
            ref={ref}
            className={cn("px-2 py-1 text-start text-base text-content-muted", className)}
            {...props}
        />
    );
});

CommandLoading.displayName = "CommandLoading";

const CommandGroup = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Group>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => {
    return (
        <CommandPrimitive.Group
            ref={ref}
            className={cn(
                "overflow-hidden p-1 text-content-primary [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-sm [&_[cmdk-group-heading]]:font-normal [&_[cmdk-group-heading]]:text-content-muted",
                className,
            )}
            {...props}
        />
    );
});

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <CommandPrimitive.Separator ref={ref} className={cn("-mx-1 h-px bg-content-tertiary/20", className)} {...props} />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
    React.ElementRef<typeof CommandPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item & PropsWithTestId>
>(({ className, ...props }, ref) => {
    return (
        <CommandPrimitive.Item
            ref={ref}
            className={cn(
                "relative flex w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-1.5 text-base outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-surface-hover data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
                className,
            )}
            {...props}
        />
    );
});

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return <span className={cn("ml-auto text-xs tracking-widest text-content-muted", className)} {...props} />;
};
CommandShortcut.displayName = "CommandShortcut";

export {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandLoading,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
    CommandShortcut,
};
